/**
 * @fileoverview Run Sample Test Call-to-Action Component
 * 
 * This component provides a CTA for users to run their first sample test
 * with step-by-step instructions for installing the npm package and
 * setting up API keys.
 * 
 * @author Claude Code
 * @since 2024-01-01
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  Play, 
  ArrowRight, 
  Terminal, 
  Key, 
  Copy, 
  CheckCircle, 
  ExternalLink,
  FileText,
  Code
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/lib/auth/useAuth';
import { trackEvent, isFeatureEnabled } from '@/lib/analytics';

/**
 * Props for the RunSampleTestCTA component
 */
interface RunSampleTestCTAProps {
  /** Optional CSS class name for custom styling */
  className?: string;
}

/**
 * Run Sample Test CTA Component
 * 
 * Provides instructions for users to run their first EvalOps test including:
 * - Installing the @evalops/evalops npm package
 * - Creating and configuring API keys
 * - Running a sample test
 * 
 * @param props - Component props
 * @returns Rendered run sample test CTA component
 */
const RunSampleTestCTA: React.FC<RunSampleTestCTAProps> = ({ className }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copiedStep, setCopiedStep] = useState<string | null>(null);
  
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();

  /**
   * Copy code snippet to clipboard
   */
  const copyToClipboard = async (text: string, stepId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStep(stepId);
      setTimeout(() => setCopiedStep(null), 2000);
      
      toast({
        title: "Copied to clipboard",
        description: "Code snippet copied successfully",
      });
      
      // Track copy event
      trackEvent('sample_test_code_copied', {
        step: stepId
      });
    } catch (error) {
      toast({
        title: "Copy failed", 
        description: "Please copy the code manually",
        variant: "destructive",
      });
    }
  };

  /**
   * Handle CTA button click
   */
  const handleCTAClick = () => {
    setIsDialogOpen(true);
    
    // Track event
    trackEvent('sample_test_cta_clicked', {
      user_authenticated: isAuthenticated
    });
  };

  /**
   * Authentication Guard - only show for authenticated users
   */
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <>
      {/* Main CTA Card */}
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <Play className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Run Your First Test</CardTitle>
              <CardDescription>
                Get started quickly with a sample test using our npm package
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Value proposition - single focused benefit */}
            <div className="flex-1">
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-blue-100 rounded-md mt-0.5">
                  <Terminal className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Quick Setup</h4>
                  <p className="text-xs text-muted-foreground">
                    Install our npm package and run tests in minutes with step-by-step instructions
                  </p>
                </div>
              </div>
            </div>
            
            {/* Primary CTA button */}
            <div className="lg:flex-shrink-0">
              <Button 
                onClick={handleCTAClick}
                className="w-full lg:w-auto lg:px-8"
                size="lg"
                aria-label="Open sample test instructions"
              >
                <Play className="mr-2 h-4 w-4" />
                Run Sample Test
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Instructions Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Run Your First EvalOps Test
            </DialogTitle>
            <DialogDescription>
              Follow these steps to install our npm package, create an API key, and run your first test
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-6">
            {/* Step 1: Install Package */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center p-0">
                  1
                </Badge>
                <h3 className="font-semibold text-lg">Install the EvalOps Package</h3>
              </div>
              <p className="text-muted-foreground ml-10">
                Install our npm package in your project directory:
              </p>
              <div className="ml-10 relative">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 font-mono text-sm">
                  <div className="flex items-center justify-between">
                    <span>npm install @evalops/evalops</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard('npm install @evalops/evalops', 'install')}
                      className="h-8 w-8 p-0"
                    >
                      {copiedStep === 'install' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Create API Key */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center p-0">
                  2
                </Badge>
                <h3 className="font-semibold text-lg">Create an API Key</h3>
              </div>
              <p className="text-muted-foreground ml-10">
                You'll need an API key to authenticate with EvalOps:
              </p>
              <div className="ml-10 space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsDialogOpen(false);
                      // Focus will return to main page where API key CTA is visible
                    }}
                    className="gap-2"
                  >
                    <Key className="h-4 w-4" />
                    Create API Key Above
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    (Use the "Create Your First API Key" button above)
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Tip:</strong> Choose scopes that include at least "read:runs" and "write:runs" for testing
                </p>
              </div>
            </div>

            {/* Step 3: Sample Test Code */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center p-0">
                  3
                </Badge>
                <h3 className="font-semibold text-lg">Create a Sample Test</h3>
              </div>
              <p className="text-muted-foreground ml-10">
                Create a simple test file to verify everything is working:
              </p>
              <div className="ml-10 relative">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">sample-test.js</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`const { EvalOps } = require('@evalops/evalops');

// Initialize EvalOps with your API key
const evalops = new EvalOps({
  apiKey: 'your-api-key-here', // Replace with your actual API key
  baseUrl: 'https://api.evalops.dev' // or your custom endpoint
});

async function runSampleTest() {
  try {
    // Create a test run
    const run = await evalops.runs.create({
      suite: 'sample-test-suite',
      environment: 'dev',
      trigger: 'manual'
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Test run created:', run.id);
    }
    
    // Simulate some test results
    await evalops.runs.addResult(run.id, {
      test: 'sample-test-case',
      status: 'passed',
      duration: 150,
      message: 'Sample test completed successfully'
    });
    
    // Complete the run
    await evalops.runs.complete(run.id, {
      status: 'passed',
      summary: 'Sample test suite completed'
    });
    
    if (import.meta.env.DEV) {
      console.log('🎉 Sample test completed successfully!');
      console.log('View your results at: https://app.evalops.dev/runs/' + run.id);
    }
    
  } catch (error) {
    console.error('❌ Test failed:', error.message);
  }
}

// Run the test
runSampleTest();`, 'sample-code')}
                      className="h-8 w-8 p-0"
                    >
                      {copiedStep === 'sample-code' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <pre className="text-xs overflow-x-auto">
                    <code>{`const { EvalOps } = require('@evalops/evalops');

// Initialize EvalOps with your API key
const evalops = new EvalOps({
  apiKey: 'your-api-key-here', // Replace with your actual API key
  baseUrl: 'https://api.evalops.dev' // or your custom endpoint
});

async function runSampleTest() {
  try {
    // Create a test run
    const run = await evalops.runs.create({
      suite: 'sample-test-suite',
      environment: 'dev',
      trigger: 'manual'
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Test run created:', run.id);
    }
    
    // Simulate some test results
    await evalops.runs.addResult(run.id, {
      test: 'sample-test-case',
      status: 'passed',
      duration: 150,
      message: 'Sample test completed successfully'
    });
    
    // Complete the run
    await evalops.runs.complete(run.id, {
      status: 'passed',
      summary: 'Sample test suite completed'
    });
    
    if (import.meta.env.DEV) {
      console.log('🎉 Sample test completed successfully!');
      console.log('View your results at: https://app.evalops.dev/runs/' + run.id);
    }
    
  } catch (error) {
    console.error('❌ Test failed:', error.message);
  }
}

// Run the test
runSampleTest();`}</code>
                  </pre>
                </div>
              </div>
            </div>

            {/* Step 4: Run the test */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center p-0">
                  4
                </Badge>
                <h3 className="font-semibold text-lg">Run the Test</h3>
              </div>
              <p className="text-muted-foreground ml-10">
                Execute your sample test:
              </p>
              <div className="ml-10 relative">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 font-mono text-sm">
                  <div className="flex items-center justify-between">
                    <span>node sample-test.js</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard('node sample-test.js', 'run')}
                      className="h-8 w-8 p-0"
                    >
                      {copiedStep === 'run' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-3">🎉 Next Steps</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Check your test results in the <strong>Runs</strong> page
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Set up alerts for failed tests in the <strong>Alerts</strong> page
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Integrate EvalOps into your CI/CD pipeline
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Explore our documentation for advanced features
                </li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RunSampleTestCTA;